// The cooler outbox (v1.84.0): a comment typed with no signal waits HERE —
// visible, honest, on this device — and sends itself when the network comes
// back. Comments only: they're append-only (replay-safe), the server already
// dedupes on clientKey, and nothing else is safe to fake offline.
//
// localStorage on purpose: a queued note is a few hundred bytes of text, the
// store survives restarts, and jsdom can test every branch without an
// IndexedDB shim. FIFO; a network failure stops the flush (the rest wait);
// a permanent refusal (4xx) drops the item and reports it — silently
// retrying a comment the server refuses would be a lie on a loop.

export interface QueuedComment {
  /** The server-side idempotency key — a flush retry can never double-post. */
  clientKey: string;
  /** WHO queued it. A note waits for ITS author's session — it must never
   *  flush under (or even show to) whoever signs in next on a shared phone. */
  uid: number;
  taskId: number;
  taskName: string;
  bodyHtml: string;
  parentId?: number;
  mentionUserIds: number[];
  channel: 'message' | 'note';
  queuedAt: string;
}

const KEY = 'cgp:outbox';

// localStorage when the browser gives us one; a process-lifetime Map when it
// doesn't (blocked storage, tests). The Map loses the queue on refresh — but
// so would a crash before the send, and a blocked localStorage would have
// thrown on every keystroke path otherwise.
const memory = new Map<string, string>();
const store = {
  get(): string | null {
    try { return typeof localStorage !== 'undefined' ? localStorage.getItem(KEY) : memory.get(KEY) ?? null; }
    catch { return memory.get(KEY) ?? null; }
  },
  set(v: string): void {
    try {
      if (typeof localStorage !== 'undefined') { localStorage.setItem(KEY, v); return; }
    } catch { /* fall through to memory */ }
    memory.set(KEY, v);
  },
};

function load(): QueuedComment[] {
  try {
    const raw = store.get();
    const list = raw ? (JSON.parse(raw) as QueuedComment[]) : [];
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

function save(list: QueuedComment[]): void {
  store.set(JSON.stringify(list));
}

export function enqueueComment(item: QueuedComment): void {
  const list = load().filter((q) => q.clientKey !== item.clientKey);
  list.push(item);
  save(list);
}

export function listQueued(taskId?: number, uid?: number): QueuedComment[] {
  let list = load();
  if (uid !== undefined) list = list.filter((q) => q.uid === uid);
  return taskId === undefined ? list : list.filter((q) => q.taskId === taskId);
}

export function removeQueued(clientKey: string): void {
  save(load().filter((q) => q.clientKey !== clientKey));
}

export function clearOutbox(): void {
  save([]);
}

export interface FlushResult {
  sent: QueuedComment[];
  /** Refused by the server (4xx) — dropped, must be surfaced to the human. */
  refused: QueuedComment[];
  /** Still queued (network died again mid-flush). */
  remaining: number;
}

/** `send` posts one item; it must THROW on failure with `status` when known
 *  (the apiClient error shape). status 0/408 = network — stop and keep;
 *  anything else = permanent — drop and report. */
/** Flush the CURRENT user's items only — foreign items (queued by someone
 *  who used this device before) are skipped in place, waiting for their
 *  author. status 0/408 (no signal) AND 401 (session died — a re-login makes
 *  the send valid again) stop the flush and keep everything; any other
 *  refusal drops the item and reports it. */
export async function flushOutbox(ownerUid: number, send: (item: QueuedComment) => Promise<void>): Promise<FlushResult> {
  const result: FlushResult = { sent: [], refused: [], remaining: 0 };
  for (const item of load()) {
    if (item.uid !== ownerUid) continue;
    try {
      await send(item);
      removeQueued(item.clientKey);
      result.sent.push(item);
    } catch (e) {
      const status = (e as { status?: number }).status ?? 0;
      if (status === 0 || status === 408 || status === 401) {
        break; // no signal / no session — everything left keeps waiting
      }
      removeQueued(item.clientKey);
      result.refused.push(item);
    }
  }
  result.remaining = load().filter((q) => q.uid === ownerUid).length;
  return result;
}
